import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MAILTO } from "../site";

const HERO_IMG =
    "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjB0cmFkZXxlbnwwfHx8fDE3OTE0NjE5MzZ8MA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative bg-zinc-950 grain overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="font-mono-label text-xs font-medium tracking-[0.25em] uppercase text-blue-500 mb-6"
                            data-testid="hero-brand-label"
                        >
                            Emira Digital — UK Website Design
                        </motion.p>

                        <h1
                            data-testid="hero-headline"
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-50 leading-[1.05]"
                        >
                            <Line delay={0.2}>Modern Websites.</Line>
                            <Line delay={0.35}>
                                Built for <span className="text-blue-500">Business.</span>
                            </Line>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.6 }}
                            className="mt-7 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl"
                            data-testid="hero-description"
                        >
                            We create clean, professional websites for UK tradespeople
                            and small businesses.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.75 }}
                            className="mt-10"
                        >
                            <a
                                data-testid="hero-get-in-touch-cta"
                                href={MAILTO}
                                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                            >
                                Get In Touch
                                <ArrowUpRight
                                    size={17}
                                    className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                />
                            </a>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1 }}
                            className="mt-12 font-mono-label text-xs tracking-widest uppercase text-zinc-600"
                        >
                            Based in the UK — working with trades nationwide
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="absolute -top-3 -left-3 w-full h-full border border-blue-600/40 rounded-2xl" />
                        <div className="relative rounded-2xl overflow-hidden aspect-[4/5] max-h-[560px] w-full">
                            <motion.img
                                src={HERO_IMG}
                                alt="Tradesperson installing wiring on site with safety gear"
                                style={{ y: imgY }}
                                className="w-full h-[120%] object-cover"
                                data-testid="hero-image"
                            />
                            <div className="absolute inset-0 bg-zinc-950/10" />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
